/**
 * Edit propagation engine for the forecast summary grid.
 *
 * Handles temporal aggregation (PTD/PTG → TTL), reverse distribution
 * (TTL → PTD/PTG), row-total spreading, and derived metric recalculation.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Maps metric names to numeric values for a single row's temporal slice. */
export type MetricValues = Record<string, number>;

/**
 * Groups related stock/inventory metrics that follow special TTL rules.
 *
 * For stock metrics, TTL is NOT simply PTD + PTG:
 * - BOP (beginning of period): TTL = PTD value (start of year = start of PTD)
 * - EOP (end of period): TTL = PTG value (end of year = end of PTG)
 * - TS (target stock): TTL = PTG value (target at year-end = PTG endpoint)
 * - OTB (open to buy): calculated via calcOtb from the accumulated TTL values
 *
 * Flows are processed in order — later flows (e.g. AUC) can reference
 * earlier flows' OTB values in their calcOtb.
 */
export interface KpiFlow {
  bop: string;
  eop: string;
  ts: string;
  otb: string;
  /**
   * Calculate OTB from the accumulated TTL values.
   * - UNIT / AT_COST flows: `(v) => v[ts] - v[eop]`
   * - AUC flow: `(v) => safeDivide(v[OTB_AT_COST], v[OTB_UNITS])`
   */
  calcOtb: (values: MetricValues) => number;
}

/** Which temporal aggregation column was edited. */
export type TemporalAgg = "PTD" | "PTG" | "TTL";

/**
 * Definition for a metric derived from other metrics.
 * Evaluated after irreducible metrics are computed.
 */
export interface DerivedMetricDef {
  /** The metric name to write (e.g. "GS1_AUR"). */
  name: string;
  /** Compute the value from the current metric values. */
  calc: (values: MetricValues) => number;
  /**
   * True for metrics reconstructed from the stock balance
   * (`EOP = BOP + INTAKE − SALES`, and `OTB` derived from `EOP`) — i.e.
   * point-in-time stock levels rather than flows or ratios. This single
   * property governs where the `calc` may be trusted:
   * - **TTL**: stock levels don't sum, so TTL uses endpoint rules
   *   (`deriveTtlFromPtdPtg`), not this derivation.
   * - **Historical PTD**: received intake is folded into stock and reported
   *   as 0, so the balance undercounts — the server's point-in-time value is
   *   used instead (see `renderColumnar`).
   * - **Temporal (weekly→period) roll-up**: excluded in favour of the
   *   server's `last`/`first` aggregation (see `temporalAggregation.ts`).
   *
   * The `calc` is applied only where the balance is valid: PTG, and a
   * forecast (future-year) PTD, where intake has not yet been received.
   */
  stockBalanceDerived?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Division that returns 0 instead of Infinity/NaN when denominator is 0. */
export function safeDivide(numerator: number, denominator: number): number {
  // Numerator first, mirroring the engine's safe_divide_pd: 0/x is 0 even
  // when the denominator is NaN (a hole), so holes don't poison zero rows.
  if (numerator === 0) return 0;
  if (denominator === 0) return 0;
  return numerator / denominator;
}

/**
 * Division mirroring `safe_divide_pd(x, y, default_value=nan)`: 0 when the
 * numerator is 0, NaN (blank) when only the denominator is 0. Use for week
 * cover — no stock is 0 weeks of cover, while no sales leaves cover undefined
 * (the app has no infinity).
 */
export function coverDivide(numerator: number, denominator: number): number {
  if (numerator === 0) return 0;
  if (denominator === 0) return Number.NaN;
  return numerator / denominator;
}

// ---------------------------------------------------------------------------
// Temporal derivation: PTD + PTG → TTL
// ---------------------------------------------------------------------------

/**
 * Derives TTL values from PTD and PTG for a single row.
 *
 * - Irreducible (flow) metrics: TTL = PTD + PTG
 * - Stock metrics per KPI flow: BOP from PTD, EOP/TS from PTG
 * - OTB: via each flow's calcOtb (flows processed in order)
 * - Endpoint ("last") metrics (e.g. STORE_SQM): TTL = PTG (end-of-period value)
 */
export function deriveTtlFromPtdPtg(
  ptd: MetricValues,
  ptg: MetricValues,
  irreducibleMetrics: string[],
  kpiFlows: KpiFlow[] = [],
  endpointMetrics: string[] = []
): MetricValues {
  const ttl: MetricValues = {};

  for (const metric of irreducibleMetrics) {
    ttl[metric] = (ptd[metric] ?? 0) + (ptg[metric] ?? 0);
  }

  for (const flow of kpiFlows) {
    ttl[flow.bop] = ptd[flow.bop] ?? 0;
    ttl[flow.eop] = ptg[flow.eop] ?? 0;
    ttl[flow.ts] = ptg[flow.ts] ?? 0;
    ttl[flow.otb] = flow.calcOtb(ttl);
  }

  for (const metric of endpointMetrics) {
    if (ptg[metric] != null) ttl[metric] = ptg[metric];
  }

  return ttl;
}

// ---------------------------------------------------------------------------
// Reverse derivation: TTL → PTD / PTG
// ---------------------------------------------------------------------------

/**
 * Derives PTD and PTG from a new TTL value.
 *
 * - ptdMutable=false (current year): PTD stays, PTG = TTL − PTD
 * - ptdMutable=true (future year): both scale proportionally by TTL_new/TTL_old.
 *   When old TTL is zero, the delta is split equally.
 */
export function derivePtdPtgFromTtl(
  ptdOld: MetricValues,
  ptgOld: MetricValues,
  ttlNew: MetricValues,
  irreducibleMetrics: string[],
  ptdMutable: boolean
): { ptd: MetricValues; ptg: MetricValues } {
  const ptd: MetricValues = { ...ptdOld };
  const ptg: MetricValues = { ...ptgOld };

  for (const metric of irreducibleMetrics) {
    const oldPtd = ptdOld[metric] ?? 0;
    const oldPtg = ptgOld[metric] ?? 0;
    const newTtl = ttlNew[metric] ?? 0;

    if (!ptdMutable) {
      ptg[metric] = newTtl - oldPtd;
    } else {
      const oldTtl = oldPtd + oldPtg;
      if (oldTtl === 0) {
        const delta = newTtl - oldTtl;
        ptd[metric] = oldPtd + delta / 2;
        ptg[metric] = oldPtg + delta / 2;
      } else {
        const ratio = newTtl / oldTtl;
        ptd[metric] = oldPtd * ratio;
        ptg[metric] = oldPtg * ratio;
      }
    }
  }

  return { ptd, ptg };
}

// ---------------------------------------------------------------------------
// Row-total distribution
// ---------------------------------------------------------------------------

/**
 * Distributes a total-row delta proportionally across child rows.
 *
 * Each child's share of the delta is proportional to its current value
 * relative to the total. When a child's value is zero (and the total is
 * also zero), the delta is split equally among all children.
 */
export function distributeToChildRows(
  totalDelta: MetricValues,
  totalOld: MetricValues,
  childRows: MetricValues[],
  metrics: string[]
): MetricValues[] {
  const n = childRows.length;
  if (n === 0) return [];

  return childRows.map((child) => {
    const delta: MetricValues = {};
    for (const metric of metrics) {
      const d = totalDelta[metric] ?? 0;
      const total = totalOld[metric] ?? 0;
      const childVal = child[metric] ?? 0;

      if (total === 0) {
        delta[metric] = d / n;
      } else {
        delta[metric] = d * (childVal / total);
      }
    }
    return delta;
  });
}

// ---------------------------------------------------------------------------
// Derived metric recalculation
// ---------------------------------------------------------------------------

/**
 * Recalculates derived metrics from their base (irreducible) values.
 *
 * Definitions are evaluated in order — later definitions can reference
 * earlier ones (e.g. GS2_MV depends on GS2 which must already be present).
 *
 * For the TTL slice, `stockBalanceDerived` metrics are skipped: stock levels
 * don't sum, so their TTL comes from endpoint rules (`deriveTtlFromPtdPtg`),
 * not the balance. To skip them for a historical PTD slice too, filter the
 * definitions before calling (see `renderColumnar`).
 */
export function recalcDerivedMetrics(
  values: MetricValues,
  definitions: DerivedMetricDef[],
  isTtl = false
): MetricValues {
  const result = { ...values };
  for (const def of definitions) {
    if (isTtl && def.stockBalanceDerived) continue;
    result[def.name] = def.calc(result);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Row-total summation
// ---------------------------------------------------------------------------

/**
 * Computes the total row by summing child rows for the given metrics.
 *
 * This is the "forward" direction: child rows → total row.
 * Used when a non-total row is edited.
 */
export function sumChildRows(
  childRows: MetricValues[],
  metrics: string[]
): MetricValues {
  const total: MetricValues = {};
  for (const metric of metrics) {
    total[metric] = childRows.reduce((sum, row) => sum + (row[metric] ?? 0), 0);
  }
  return total;
}

// ---------------------------------------------------------------------------
// Metric value helpers
// ---------------------------------------------------------------------------

/** Applies a delta to metric values, returning a new object. */
export function applyDelta(
  values: MetricValues,
  delta: MetricValues
): MetricValues {
  const result = { ...values };
  for (const key of Object.keys(delta)) {
    result[key] = (result[key] ?? 0) + delta[key];
  }
  return result;
}

// ---------------------------------------------------------------------------
// Top-level edit orchestration
// ---------------------------------------------------------------------------

/**
 * Configuration for the edit propagation engine.
 * Passed in by the page component; encodes which metrics exist and how
 * they relate to each other.
 */
export interface EditConfig {
  /** Metrics that sum for temporal aggregation (sales + intake sub-components). */
  irreducibleMetrics: string[];
  /** Stock metric groups with special TTL rules. Processed in order. */
  kpiFlows: KpiFlow[];
  /** Endpoint ("last") metrics whose TTL is the end-of-period (PTG) value. */
  endpointMetrics: string[];
  /** Derived metrics to recalculate after changes. */
  derivedMetrics: DerivedMetricDef[];
  /** Whether PTD is mutable (true for future years). */
  ptdMutable: boolean;
}

/**
 * Grid state: PTD and PTG values for every cell.
 *
 * Cells hold **only the client-owned irreducible metrics** (sales + intake
 * components) — the editable state. Everything else lives elsewhere by
 * ownership: server-computed values stay in the immutable server payload,
 * and derived metrics are recomputed at render (`renderColumnar`), never
 * stored. Putting other metrics in cells is a bug — the engine sums and
 * distributes cells as pure irreducible vectors.
 *
 * Convention:
 * - Last table index → TTL (total) table
 * - Last row index in each table → TTL (total) row
 *
 * TTL values are always derived from PTD + PTG and are never stored.
 */
export interface GridData {
  ptd: MetricValues[][];
  ptg: MetricValues[][];
}

// ---------------------------------------------------------------------------
// processEdit helpers (extracted for max-lines-per-function compliance)
// ---------------------------------------------------------------------------

/** Internal state shared between processEdit and its helpers. */
interface EditState {
  ptd: MetricValues[][];
  ptg: MetricValues[][];
  lastRow: number;
  lastTable: number;
  irreducibleMetrics: string[];
}

function getSlice(state: EditState, agg: string): MetricValues[][] {
  return agg === "PTD" ? state.ptd : state.ptg;
}

function resumTotalRow(state: EditState, ti: number): void {
  const { ptd, ptg, lastRow, irreducibleMetrics } = state;
  ptd[ti][lastRow] = sumChildRows(
    ptd[ti].slice(0, lastRow),
    irreducibleMetrics
  );
  ptg[ti][lastRow] = sumChildRows(
    ptg[ti].slice(0, lastRow),
    irreducibleMetrics
  );
}

function resumTotalTable(state: EditState, ri: number): void {
  const { ptd, ptg, lastTable, irreducibleMetrics } = state;
  ptd[lastTable][ri] = sumChildRows(
    ptd.slice(0, lastTable).map((t) => t[ri]),
    irreducibleMetrics
  );
  ptg[lastTable][ri] = sumChildRows(
    ptg.slice(0, lastTable).map((t) => t[ri]),
    irreducibleMetrics
  );
}

/** Distributes total-row delta to child rows (row-total propagation). */
function ttlOfRow(
  ptd: MetricValues,
  ptg: MetricValues,
  irreducibleMetrics: string[]
): MetricValues {
  const out: MetricValues = {};
  for (const m of irreducibleMetrics) {
    out[m] = (ptd[m] ?? 0) + (ptg[m] ?? 0);
  }
  return out;
}

function propagateRowTotal(
  state: EditState,
  data: GridData,
  tableIndex: number,
  agg: TemporalAgg,
  ptdMutable: boolean
): void {
  const { lastRow, irreducibleMetrics } = state;

  if (agg === "TTL") {
    // Legacy semantics: spread the TTL delta by the children's TTL shares
    // (a planner cutting the year total expects the cut distributed by row
    // size), then let each child's own PTD/PTG split absorb its row delta —
    // PTD frozen in the current year, proportional when ptdMutable.
    const oldTotalTtl = ttlOfRow(
      data.ptd[tableIndex][lastRow],
      data.ptg[tableIndex][lastRow],
      irreducibleMetrics
    );
    const newTotalTtl = ttlOfRow(
      state.ptd[tableIndex][lastRow],
      state.ptg[tableIndex][lastRow],
      irreducibleMetrics
    );
    const ttlDelta: MetricValues = {};
    for (const m of irreducibleMetrics) {
      ttlDelta[m] = (newTotalTtl[m] ?? 0) - (oldTotalTtl[m] ?? 0);
    }

    const childTtls = data.ptd[tableIndex]
      .slice(0, lastRow)
      .map((row, ri) =>
        ttlOfRow(row, data.ptg[tableIndex][ri], irreducibleMetrics)
      );
    const childDeltas = distributeToChildRows(
      ttlDelta,
      oldTotalTtl,
      childTtls,
      irreducibleMetrics
    );
    for (let ri = 0; ri < lastRow; ri++) {
      const derived = derivePtdPtgFromTtl(
        data.ptd[tableIndex][ri],
        data.ptg[tableIndex][ri],
        applyDelta(childTtls[ri], childDeltas[ri]),
        irreducibleMetrics,
        ptdMutable
      );
      state.ptd[tableIndex][ri] = derived.ptd;
      state.ptg[tableIndex][ri] = derived.ptg;
    }
    return;
  }

  const oldSlice =
    agg === "PTD"
      ? data.ptd[tableIndex][lastRow]
      : data.ptg[tableIndex][lastRow];
  const newSlice =
    agg === "PTD"
      ? state.ptd[tableIndex][lastRow]
      : state.ptg[tableIndex][lastRow];

  const delta: MetricValues = {};
  for (const m of irreducibleMetrics) {
    delta[m] = (newSlice[m] ?? 0) - (oldSlice[m] ?? 0);
  }

  const slice = getSlice(state, agg);
  const childDeltas = distributeToChildRows(
    delta,
    oldSlice,
    slice[tableIndex].slice(0, lastRow),
    irreducibleMetrics
  );
  for (let ri = 0; ri < lastRow; ri++) {
    slice[tableIndex][ri] = applyDelta(slice[tableIndex][ri], childDeltas[ri]);
  }
}

/** Distributes TTL table delta to child tables (table-total propagation). */
function propagateTableTotal(
  state: EditState,
  data: GridData,
  numRows: number,
  agg: TemporalAgg,
  ptdMutable: boolean
): void {
  const { lastTable, irreducibleMetrics } = state;
  if (agg === "TTL") {
    // Same TTL-share semantics as propagateRowTotal, across child tables.
    for (let ri = 0; ri < numRows; ri++) {
      const oldTotalTtl = ttlOfRow(
        data.ptd[lastTable][ri],
        data.ptg[lastTable][ri],
        irreducibleMetrics
      );
      const newTotalTtl = ttlOfRow(
        state.ptd[lastTable][ri],
        state.ptg[lastTable][ri],
        irreducibleMetrics
      );
      const ttlDelta: MetricValues = {};
      for (const m of irreducibleMetrics) {
        ttlDelta[m] = (newTotalTtl[m] ?? 0) - (oldTotalTtl[m] ?? 0);
      }
      if (irreducibleMetrics.every((m) => ttlDelta[m] === 0)) continue;

      const childTtls = data.ptd
        .slice(0, lastTable)
        .map((t, ti) => ttlOfRow(t[ri], data.ptg[ti][ri], irreducibleMetrics));
      const childDeltas = distributeToChildRows(
        ttlDelta,
        oldTotalTtl,
        childTtls,
        irreducibleMetrics
      );
      for (let ti = 0; ti < lastTable; ti++) {
        const derived = derivePtdPtgFromTtl(
          data.ptd[ti][ri],
          data.ptg[ti][ri],
          applyDelta(childTtls[ti], childDeltas[ti]),
          irreducibleMetrics,
          ptdMutable
        );
        state.ptd[ti][ri] = derived.ptd;
        state.ptg[ti][ri] = derived.ptg;
      }
    }
    for (let ti = 0; ti < lastTable; ti++) {
      resumTotalRow(state, ti);
    }
    return;
  }
  for (let ri = 0; ri < numRows; ri++) {
    for (const sliceAgg of ["PTD", "PTG"] as const) {
      const slice = getSlice(state, sliceAgg);
      const oldTotal = data[sliceAgg === "PTD" ? "ptd" : "ptg"][lastTable][ri];
      const newTotal = slice[lastTable][ri];
      const delta: MetricValues = {};
      for (const m of irreducibleMetrics) {
        delta[m] = (newTotal[m] ?? 0) - (oldTotal[m] ?? 0);
      }
      if (irreducibleMetrics.every((m) => delta[m] === 0)) continue;

      const childTableSlices = data[sliceAgg === "PTD" ? "ptd" : "ptg"]
        .slice(0, lastTable)
        .map((t) => t[ri]);
      const childDeltas = distributeToChildRows(
        delta,
        oldTotal,
        childTableSlices,
        irreducibleMetrics
      );
      for (let ti = 0; ti < lastTable; ti++) {
        slice[ti][ri] = applyDelta(slice[ti][ri], childDeltas[ti]);
      }
    }
  }
  for (let ti = 0; ti < lastTable; ti++) {
    resumTotalRow(state, ti);
  }
}

// ---------------------------------------------------------------------------
// Top-level edit orchestration
// ---------------------------------------------------------------------------

/** Parameters for a single cell edit. */
interface ProcessEditParams {
  /** Current grid state. */
  data: GridData;
  /** Which table the edit occurred in. */
  tableIndex: number;
  /** Which row the edit occurred in. */
  rowIndex: number;
  /** The metric that was edited. */
  metric: string;
  /** Which temporal aggregation was edited (PTD / PTG / TTL). */
  agg: TemporalAgg;
  /** The new value entered by the user. */
  newValue: number;
  /** Engine configuration (irreducible metrics, KPI flows, etc.). */
  config: EditConfig;
}

/**
 * Processes a single cell edit and propagates changes through the grid.
 *
 * Handles four propagation axes:
 * 1. Temporal: PTD/PTG ↔ TTL within the edited cell's row
 * 2. Row totals: child rows ↔ total row within the edited table
 * 3. Table totals: child tables ↔ TTL table for the affected rows
 * 4. Derived metrics: recalculated for all affected cells
 */
export function processEdit(params: ProcessEditParams): GridData {
  const { data, tableIndex, rowIndex, metric, agg, newValue, config } = params;
  const numRows = data.ptd[0].length;
  const isTotalRow = rowIndex === numRows - 1;
  const isTotalTable = tableIndex === data.ptd.length - 1;

  const { irreducibleMetrics, kpiFlows, endpointMetrics, ptdMutable } = config;

  const state: EditState = {
    ptd: data.ptd.map((t) => t.map((r) => ({ ...r }))),
    ptg: data.ptg.map((t) => t.map((r) => ({ ...r }))),
    lastRow: numRows - 1,
    lastTable: data.ptd.length - 1,
    irreducibleMetrics
  };

  // 1. Apply the edit
  if (agg === "PTD" || agg === "PTG") {
    const slice = getSlice(state, agg);
    slice[tableIndex][rowIndex] = {
      ...slice[tableIndex][rowIndex],
      [metric]: newValue
    };
  } else {
    const oldPtd = data.ptd[tableIndex][rowIndex];
    const oldPtg = data.ptg[tableIndex][rowIndex];
    const oldTtl = deriveTtlFromPtdPtg(
      oldPtd,
      oldPtg,
      irreducibleMetrics,
      kpiFlows,
      endpointMetrics
    );
    const newTtl = { ...oldTtl, [metric]: newValue };
    const derived = derivePtdPtgFromTtl(
      oldPtd,
      oldPtg,
      newTtl,
      irreducibleMetrics,
      ptdMutable
    );
    state.ptd[tableIndex][rowIndex] = derived.ptd;
    state.ptg[tableIndex][rowIndex] = derived.ptg;
  }

  // 2. Row-total propagation
  if (isTotalRow) {
    propagateRowTotal(state, data, tableIndex, agg, ptdMutable);
  } else {
    resumTotalRow(state, tableIndex);
  }

  // 3. Table-total propagation
  if (isTotalTable) {
    propagateTableTotal(state, data, numRows, agg, ptdMutable);
  } else {
    for (let ri = 0; ri < numRows; ri++) {
      resumTotalTable(state, ri);
    }
  }

  return { ptd: state.ptd, ptg: state.ptg };
}
